// ============================================================================
// Type-Safe Enum Validators
// ============================================================================
// These validators ensure that numeric values from ECS components are valid
// enum members before use, replacing unsafe `as TypeValue` casts.
// ============================================================================
import { CauseOfDeath, RotStage, } from '../entities/corpse.js';
import { ItemType } from '../items/types.js';
import { NpcType } from '../npcs/types.js';
import { ObjectCategory, ObjectType, } from '../objects/types.js';
// ============================================================================
// Generic Validator Factory
// ============================================================================
/**
 * Creates a Set of valid values from an enum-like const object.
 * Used for O(1) validation lookups.
 */
const createValidValueSet = (enumObj) => {
    return new Set(Object.values(enumObj));
};
// ============================================================================
// ObjectType Validators
// ============================================================================
const VALID_OBJECT_TYPES = createValidValueSet(ObjectType);
/**
 * Type guard to check if a number is a valid ObjectTypeValue.
 */
export const isValidObjectType = (value) => {
    return VALID_OBJECT_TYPES.has(value);
};
/**
 * Converts a number to ObjectTypeValue, returning NONE if invalid.
 */
export const toObjectType = (value) => {
    return isValidObjectType(value) ? value : ObjectType.NONE;
};
/**
 * Asserts that a number is a valid ObjectTypeValue, throwing if not.
 */
export const assertObjectType = (value) => {
    if (!isValidObjectType(value)) {
        throw new Error(`Invalid ObjectType value: ${value}`);
    }
    return value;
};
// ============================================================================
// ObjectCategory Validators
// ============================================================================
const VALID_OBJECT_CATEGORIES = createValidValueSet(ObjectCategory);
/**
 * Type guard to check if a number is a valid ObjectCategoryValue.
 */
export const isValidObjectCategory = (value) => {
    return VALID_OBJECT_CATEGORIES.has(value);
};
/**
 * Converts a number to ObjectCategoryValue, returning NONE if invalid.
 */
export const toObjectCategory = (value) => {
    return isValidObjectCategory(value) ? value : ObjectCategory.NONE;
};
// ============================================================================
// ItemType Validators
// ============================================================================
const VALID_ITEM_TYPES = createValidValueSet(ItemType);
/**
 * Type guard to check if a number is a valid ItemTypeValue.
 */
export const isValidItemType = (value) => {
    return VALID_ITEM_TYPES.has(value);
};
/**
 * Converts a number to ItemTypeValue, returning NONE if invalid.
 */
export const toItemType = (value) => {
    return isValidItemType(value) ? value : ItemType.NONE;
};
/**
 * Asserts that a number is a valid ItemTypeValue, throwing if not.
 */
export const assertItemType = (value) => {
    if (!isValidItemType(value)) {
        throw new Error(`Invalid ItemType value: ${value}`);
    }
    return value;
};
// ============================================================================
// NpcType Validators
// ============================================================================
const VALID_ANIMAL_TYPES = createValidValueSet(NpcType);
/**
 * Type guard to check if a number is a valid NpcTypeValue.
 */
export const isValidNpcType = (value) => {
    return VALID_ANIMAL_TYPES.has(value);
};
/**
 * Converts a number to NpcTypeValue, returning NONE if invalid.
 */
export const toNpcType = (value) => {
    return isValidNpcType(value) ? value : NpcType.NONE;
};
/**
 * Asserts that a number is a valid NpcTypeValue, throwing if not.
 */
export const assertNpcType = (value) => {
    if (!isValidNpcType(value)) {
        throw new Error(`Invalid NpcType value: ${value}`);
    }
    return value;
};
// ============================================================================
// CauseOfDeath Validators
// ============================================================================
const VALID_CAUSE_OF_DEATH = createValidValueSet(CauseOfDeath);
/**
 * Type guard to check if a number is a valid CauseOfDeathValue.
 */
export const isValidCauseOfDeath = (value) => {
    return VALID_CAUSE_OF_DEATH.has(value);
};
/**
 * Converts a number to CauseOfDeathValue, returning UNKNOWN if invalid.
 */
export const toCauseOfDeath = (value) => {
    return isValidCauseOfDeath(value) ? value : CauseOfDeath.UNKNOWN;
};
// ============================================================================
// RotStage Validators
// ============================================================================
const VALID_ROT_STAGES = createValidValueSet(RotStage);
/**
 * Type guard to check if a number is a valid RotStageValue.
 */
export const isValidRotStage = (value) => {
    return VALID_ROT_STAGES.has(value);
};
/**
 * Converts a number to RotStageValue, returning FRESH if invalid.
 */
export const toRotStage = (value) => {
    return isValidRotStage(value) ? value : RotStage.FRESH;
};
//# sourceMappingURL=validators.js.map